/**
 * Alshallal Pastry Company - Edit Employee Page
 * Phase 2: Edit employee with reason prompt for sensitive fields
 * Phase 18.2.2: Approving Manager ID field (matrix approval routing)
 * Phase 24.14: Full nationalities list
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  User,
  Briefcase,
  CreditCard,
  Building2,
  Loader2,
  AlertCircle,
  RefreshCw,
  Shield,
  Users,
  Database,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { listReturnPath } from '../../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { NATIONALITIES } from '@/constants/nationalities';
import { ManagerSelect } from '@/components/employees/ManagerSelect';
import { EmployeeFormField } from '@/components/employees/EmployeeFormField';
import {
  STATUSES,
  CONTRACT_TYPES,
  GENDERS,
  DEPARTMENTS,
  BANKS,
} from '@/constants/employeeForm';
import {
  validateEmployeeForm,
  firstTabWithError,
} from '@/lib/employeeFormValidation';
import { AnnualConfigurationSummary } from '@/components/employees/benefits/AnnualConfigurationSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sensitive fields that require reason for change
const SENSITIVE_FIELDS = [
  'national_id', 'iqama', 'iban',
  'basic_salary', 'housing_allowance', 'transportation_allowance', 'other_allowances',
  'status'
];

const EmployeeEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();

  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  // Reason dialog for sensitive field changes
  const [reasonDialog, setReasonDialog] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [pendingChanges, setPendingChanges] = useState(null);

  // Phase 18.2.2: Employee list for manager selection
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  // Fallback labels for currently-assigned managers when they are no longer
  // in the filtered manager-eligible pool (e.g. their role was changed).
  const [currentManagerFallback, setCurrentManagerFallback] = useState(null);
  const [currentApprovingManagerFallback, setCurrentApprovingManagerFallback] = useState(null);
  
  // Phase 18.3.1: Department list for department selection
  const [departmentsList, setDepartmentsList] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Phase 24.8: Branch list for branch selection
  const [branchesList, setBranchesList] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);

  // Phase 18.8.1: Custom fields
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);

  // Phase 18.8.1: RBAC for custom fields
  const canEditCustomFields = hasRole('super_admin', 'hr_manager');

  const [formData, setFormData] = useState({
    // Personal
    first_name: '',
    first_name_ar: '',
    middle_name: '',
    middle_name_ar: '',
    last_name: '',
    last_name_ar: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    phone: '',
    personal_email: '',
    address: '',
    city: '',
    // Identity
    national_id: '',
    national_id_expiry: '',
    iqama: '',
    iqama_expiry: '',
    passport_number: '',
    passport_expiry: '',
    // Employment
    employee_number: '',
    job_title: '',
    job_title_ar: '',
    department: '',
    department_ar: '',
    department_id: '',  // Phase 18.3.1
    branch_id: '',  // Phase 24.8: Branch
    hire_date: '',
    contract_type: 'permanent',
    contract_start_date: '',
    contract_end_date: '',
    location: '',
    status: 'active',
    manager_id: '',
    approving_manager_id: '',
    daily_working_hours: '',  // Phase 22: Required field - must be explicitly set
    // Bank
    bank_name: '',
    iban: '',
    account_holder_name: '',
    // Compensation
    basic_salary: '',
    housing_allowance: '',
    transportation_allowance: '',
    other_allowances: '',
  });

  const canEditSensitive = hasRole('hr_admin', 'super_admin', 'hr_manager');
  // The owner's instruction (2026-09-17): the annual leave entitlement and the
  // two accrual dates belong to HR Manager, HR Admin and Super Admin only.
  const canEditAnnualLeave = hasRole('hr_manager', 'hr_admin', 'super_admin');

  const getLabel = (item) => language === 'ar' ? item.label.ar : item.label.en;

  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/employees/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const emp = response.data;
        const flatData = {
          // Personal
          first_name: emp.personal?.first_name || '',
          first_name_ar: emp.personal?.first_name_ar || '',
          middle_name: emp.personal?.middle_name || '',
          middle_name_ar: emp.personal?.middle_name_ar || '',
          last_name: emp.personal?.last_name || '',
          last_name_ar: emp.personal?.last_name_ar || '',
          date_of_birth: emp.personal?.date_of_birth || '',
          gender: emp.personal?.gender || '',
          nationality: emp.personal?.nationality || '',
          phone: emp.personal?.phone || '',
          personal_email: emp.personal?.personal_email || '',
          address: emp.personal?.address || '',
          city: emp.personal?.city || '',
          // Identity - Note: these are masked, can only be replaced
          national_id: '', // Leave empty, user must enter new value to change
          national_id_expiry: emp.identity?.national_id_expiry || '',
          iqama: '', // Leave empty
          iqama_expiry: emp.identity?.iqama_expiry || '',
          passport_number: emp.identity?.passport_number || '',
          passport_expiry: emp.identity?.passport_expiry || '',
          // Employment
          employee_number: emp.employment?.employee_number || '',
          job_title: emp.employment?.job_title || '',
          job_title_ar: emp.employment?.job_title_ar || '',
          department: emp.employment?.department || '',
          department_ar: emp.employment?.department_ar || '',
          department_id: emp.employment?.department_id || '',  // Phase 18.3.1
          branch_id: emp.employment?.branch_id || '',  // Phase 24.8
          hire_date: emp.employment?.hire_date || '',
          contract_type: emp.employment?.contract_type || 'permanent',
          contract_start_date: emp.employment?.contract_start_date || '',
          contract_end_date: emp.employment?.contract_end_date || '',
          location: emp.employment?.location || '',
          status: emp.employment?.status || 'active',
          manager_id: emp.employment?.manager_id || '',
          approving_manager_id: emp.employment?.approving_manager_id || '',
          daily_working_hours: (emp.employment?.daily_working_hours || 8).toString(),  // Phase 22
          // Bank
          bank_name: emp.bank?.bank_name || '',
          iban: '', // Leave empty
          account_holder_name: emp.bank?.account_holder_name || '',
          // Compensation
          basic_salary: emp.compensation?.basic_salary?.toString() || '',
          housing_allowance: emp.compensation?.housing_allowance?.toString() || '',
          transportation_allowance: emp.compensation?.transportation_allowance?.toString() || '',
          other_allowances: emp.compensation?.other_allowances?.toString() || '',
        };

        setFormData(flatData);
        setOriginalData({ ...flatData, employee: emp });
        
        // Phase 18.8.1: Set custom field values from employee data
        setCustomFieldValues(emp.custom_fields || {});

        // Fetch fallback display info for currently-assigned managers so the
        // ManagerSelect trigger renders correctly even if the manager is no
        // longer in the filtered manager-eligible pool.
        const ids = [emp.employment?.manager_id, emp.employment?.approving_manager_id]
          .filter(Boolean)
          .filter((mid, i, arr) => arr.indexOf(mid) === i); // dedupe
        const fallbacks = {};
        await Promise.all(ids.map(async (mid) => {
          try {
            const r = await axios.get(`${API_URL}/employees/${mid}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const m = r.data || {};
            fallbacks[mid] = {
              id: mid,
              first_name: m.personal?.first_name || '',
              first_name_ar: m.personal?.first_name_ar || '',
              last_name: m.personal?.last_name || '',
              last_name_ar: m.personal?.last_name_ar || '',
              department: m.employment?.department || '',
              job_title: m.employment?.job_title || '',
            };
          } catch (_) { /* non-blocking */ }
        }));
        if (emp.employment?.manager_id && fallbacks[emp.employment.manager_id]) {
          setCurrentManagerFallback(fallbacks[emp.employment.manager_id]);
        }
        if (emp.employment?.approving_manager_id && fallbacks[emp.employment.approving_manager_id]) {
          setCurrentApprovingManagerFallback(fallbacks[emp.employment.approving_manager_id]);
        }
      } catch (err) {
        console.error('Failed to fetch employee:', err);
        setErrors({ submit: err.response?.data?.detail?.error || 'Failed to load employee' });
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id, token]);

  // Phase 18.8.1: Fetch custom field definitions
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        setLoadingCustomFields(true);
        const response = await axios.get(`${API_URL}/custom-fields/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomFieldDefinitions(response.data.fields || []);
      } catch (err) {
        console.error('Failed to fetch custom fields:', err);
        // Non-blocking - custom fields are optional
      } finally {
        setLoadingCustomFields(false);
      }
    };
    
    fetchCustomFields();
  }, [token]);

  // Phase 18.2.2: Fetch manager candidates (super_admin / hr_manager / manager)
  // for Direct & Approving Manager dropdowns. HR Admin/Super Admin only.
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!canEditSensitive) return;
      
      try {
        setLoadingEmployees(true);
        const response = await axios.get(`${API_URL}/employees/managers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Exclude the current employee from the manager pool
        const empList = (response.data.managers || []).filter(e => e.id !== id);
        setEmployees(empList);
      } catch (err) {
        console.error('Failed to fetch manager candidates:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    
    fetchEmployees();
  }, [token, id, canEditSensitive]);

  // Phase 24.9: Fetch departments for hierarchical dropdown (all users need this for department selection)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        // Phase 24.9: Use hierarchy endpoint
        const response = await axios.get(`${API_URL}/departments/hierarchy/tree`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Flatten hierarchy for dropdown
        const hierarchy = response.data.hierarchy || [];
        const flatDepts = [];
        hierarchy.forEach(main => {
          flatDepts.push({ ...main, isMain: true, level: 0 });
          (main.sub_departments || []).forEach(sub => {
            flatDepts.push({ ...sub, isMain: false, level: 1, parentName: main.name_en });
          });
        });
        setDepartmentsList(flatDepts);
      } catch (err) {
        console.error('Failed to fetch departments hierarchy:', err);
        // Fallback to flat list
        try {
          const response = await axios.get(`${API_URL}/departments/`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setDepartmentsList(response.data.departments || []);
        } catch (fallbackErr) {
          console.error('Failed to fetch departments:', fallbackErr);
        }
      } finally {
        setLoadingDepartments(false);
      }
    };
    
    fetchDepartments();
  }, [token]);

  // Phase 24.8: Fetch branches for dropdown (HR Admin/Super Admin only)
  useEffect(() => {
    const fetchBranches = async () => {
      if (!canEditSensitive) return;
      
      try {
        setLoadingBranches(true);
        const response = await axios.get(`${API_URL}/branches/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setBranchesList(response.data.branches || []);
      } catch (err) {
        console.error('Failed to fetch branches:', err);
      } finally {
        setLoadingBranches(false);
      }
    };
    
    fetchBranches();
  }, [token, canEditSensitive]);

  // Phase 24.8: Create new branch inline
  const handleCreateBranch = async () => {
    if (!newBranchName.trim()) return;
    
    setCreatingBranch(true);
    try {
      const response = await axios.post(`${API_URL}/branches/`, 
        { name_en: newBranchName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newBranch = response.data;
      setBranchesList(prev => [...prev, newBranch]);
      handleChange('branch_id', newBranch.id);
      setNewBranchName('');
    } catch (err) {
      console.error('Failed to create branch:', err);
    } finally {
      setCreatingBranch(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = validateEmployeeForm(formData, { isRTL, mode: 'edit' });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isRTL]);

  const checkSensitiveChanges = () => {
    if (!originalData) return false;
    
    // Check if any sensitive field changed
    for (const field of SENSITIVE_FIELDS) {
      const newValue = formData[field];
      const origValue = originalData[field];
      
      // For identity/bank fields that were empty (masked), only consider change if new value is provided
      if (['national_id', 'iqama', 'iban'].includes(field)) {
        if (newValue && newValue.trim() !== '') {
          return true;
        }
      } else if (newValue !== origValue && (newValue || origValue)) {
        return true;
      }
    }
    return false;
  };

  const buildPayload = () => {
    const payload = {};

    // Personal changes
    const personalChanges = {};
    const personalFields = ['first_name', 'first_name_ar', 'middle_name', 'middle_name_ar', 
                           'last_name', 'last_name_ar', 'date_of_birth', 'gender', 
                           'nationality', 'phone', 'personal_email', 'address', 'city'];
    personalFields.forEach(f => {
      if (formData[f] !== originalData[f]) {
        personalChanges[f] = formData[f] || null;
      }
    });
    if (Object.keys(personalChanges).length > 0) {
      payload.personal = personalChanges;
    }

    // Identity changes (only if new values provided)
    const identityChanges = {};
    if (formData.national_id && formData.national_id.trim()) {
      identityChanges.national_id = formData.national_id;
    }
    if (formData.national_id_expiry !== originalData.national_id_expiry) {
      identityChanges.national_id_expiry = formData.national_id_expiry || null;
    }
    if (formData.iqama && formData.iqama.trim()) {
      identityChanges.iqama = formData.iqama;
    }
    if (formData.iqama_expiry !== originalData.iqama_expiry) {
      identityChanges.iqama_expiry = formData.iqama_expiry || null;
    }
    if (formData.passport_number !== originalData.passport_number) {
      identityChanges.passport_number = formData.passport_number || null;
    }
    if (formData.passport_expiry !== originalData.passport_expiry) {
      identityChanges.passport_expiry = formData.passport_expiry || null;
    }
    if (Object.keys(identityChanges).length > 0) {
      payload.identity = identityChanges;
    }

    // Employment changes
    const employmentChanges = {};
    const employmentFields = ['employee_number', 'job_title', 'job_title_ar', 'department',
                             'department_ar', 'department_id', 'branch_id', 'hire_date', 'contract_type', 'contract_start_date',
                             'contract_end_date', 'location', 'status', 'manager_id', 'approving_manager_id', 'daily_working_hours'];
    employmentFields.forEach(f => {
      if (formData[f] !== originalData[f]) {
        // Phase 22: Convert daily_working_hours to integer
        if (f === 'daily_working_hours') {
          employmentChanges[f] = parseInt(formData[f]) || 8;
        } else {
          employmentChanges[f] = formData[f] || null;
        }
      }
    });
    if (Object.keys(employmentChanges).length > 0) {
      payload.employment = employmentChanges;
    }

    // Bank changes
    const bankChanges = {};
    if (formData.bank_name !== originalData.bank_name) {
      bankChanges.bank_name = formData.bank_name || null;
    }
    if (formData.iban && formData.iban.trim()) {
      bankChanges.iban = formData.iban.replace(/\s/g, '').toUpperCase();
    }
    if (formData.account_holder_name !== originalData.account_holder_name) {
      bankChanges.account_holder_name = formData.account_holder_name || null;
    }
    if (Object.keys(bankChanges).length > 0) {
      payload.bank = bankChanges;
    }

    // Compensation changes
    const compChanges = {};
    const compFields = ['basic_salary', 'housing_allowance', 'transportation_allowance', 'other_allowances'];
    compFields.forEach(f => {
      if (formData[f] !== originalData[f]) {
        compChanges[f] = formData[f] ? parseFloat(formData[f]) : null;
      }
    });
    if (Object.keys(compChanges).length > 0) {
      payload.compensation = compChanges;
    }

    // Phase 18.8.1: Custom fields changes
    if (Object.keys(customFieldValues).length > 0) {
      payload.custom_fields = customFieldValues;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // The offending field is often on a tab the user is not looking at — a
      // toast alone then reads as "Save does nothing". Jump to the tab that
      // owns the first error before scrolling to it.
      const failures = validateEmployeeForm(formData, { isRTL, mode: 'edit' });
      const tab = firstTabWithError(failures);
      if (tab) setActiveTab(tab);
      setTimeout(() => {
        const firstError = document.querySelector('.text-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      toast.error(isRTL ? 'يرجى تصحيح الأخطاء قبل الحفظ' : 'Please fix the errors before saving');
      return;
    }

    const payload = buildPayload();
    
    if (Object.keys(payload).length === 0) {
      toast.info(isRTL ? 'لم يتم إجراء أي تغييرات' : 'No changes were made');
      navigate(`/employees/${id}`);
      return;
    }

    // Check if sensitive fields changed
    const hasSensitiveChanges = checkSensitiveChanges();
    
    if (hasSensitiveChanges && canEditSensitive) {
      setPendingChanges(payload);
      setReasonDialog(true);
      return;
    }

    await submitChanges(payload, null);
  };

  const submitChanges = async (payload, reason) => {
    setSaving(true);
    setErrors({});

    try {
      const url = reason 
        ? `${API_URL}/employees/${id}?reason=${encodeURIComponent(reason)}`
        : `${API_URL}/employees/${id}`;

      await axios.patch(url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(isRTL ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully');
      navigate(`/employees/${id}`);
    } catch (err) {
      console.error('Failed to update employee:', err);
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.detail || 'Failed to update employee';
      setErrors({ submit: errorMsg });
      toast.error(isRTL ? 'فشل في حفظ التغييرات' : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleReasonConfirm = async () => {
    if (changeReason.length < 10) return;
    
    setReasonDialog(false);
    await submitChanges(pendingChanges, changeReason);
    setPendingChanges(null);
    setChangeReason('');
  };

  const renderField = (field, label, type = 'text', options = {}) => {
    const isSensitive = SENSITIVE_FIELDS.includes(field);
    return (
      <EmployeeFormField
        field={field}
        label={label}
        type={type}
        value={formData[field]}
        onChange={handleChange}
        error={errors[field]}
        required={options.required}
        disabled={options.disabled}
        placeholder={options.placeholder}
        sensitive={isSensitive}
        canEditSensitive={canEditSensitive}
        isRTL={isRTL}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!originalData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {isRTL ? 'خطأ' : 'Error'}
            </h2>
            <p className="text-slate-600 mb-4">{errors.submit || 'Employee not found'}</p>
            <Button onClick={() => navigate(listReturnPath('employees', '/employees'))}>
              {isRTL ? 'العودة للموظفين' : 'Back to Employees'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-edit-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/employees/${id}`)}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'تعديل الموظف' : 'Edit Employee'}
          </h1>
          <p className="text-slate-600 mt-1">
            {originalData.employee?.personal?.first_name} {originalData.employee?.personal?.last_name}
          </p>
        </div>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">
              {isRTL ? 'فشل التحديث' : 'Update failed'}
            </p>
            <p className="text-red-600 text-sm mt-1">{errors.submit}</p>
          </div>
        </div>
      )}

      {/* Sensitive Fields Notice */}
      {canEditSensitive && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">
              {isRTL ? 'تحرير الحقول الحساسة' : 'Sensitive Field Editing'}
            </p>
            <p className="mt-1">
              {isRTL 
                ? 'تغيير الهوية الوطنية أو الإقامة أو الآيبان أو الراتب أو الحالة يتطلب سبباً ويتم تسجيله.' 
                : 'Changes to National ID, Iqama, IBAN, Compensation, or Status require a reason and will be logged.'}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full overflow-x-auto gap-1 p-1">
            <TabsTrigger value="personal" className="flex items-center gap-2 flex-shrink-0">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'شخصي' : 'Personal'}</span>
            </TabsTrigger>
            <TabsTrigger value="identity" className="flex items-center gap-2 flex-shrink-0">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'الهوية' : 'Identity'}</span>
            </TabsTrigger>
            <TabsTrigger value="employment" data-testid="employee-edit-employment-tab" className="flex items-center gap-2 flex-shrink-0">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'الوظيفة' : 'Employment'}</span>
            </TabsTrigger>
            <TabsTrigger value="bank" className="flex items-center gap-2 flex-shrink-0">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'البنك' : 'Bank'}</span>
            </TabsTrigger>
            <TabsTrigger value="compensation" className="flex items-center gap-2 flex-shrink-0">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'التعويضات' : 'Compensation'}</span>
            </TabsTrigger>
            {/* Phase 18.8.1: Custom Fields Tab */}
            {customFieldDefinitions.length > 0 && (
              <TabsTrigger value="custom" className="flex items-center gap-2 flex-shrink-0">
                <Database className="h-4 w-4" />
                <span className="hidden sm:inline">{isRTL ? 'مخصص' : 'Custom'}</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'المعلومات الشخصية' : 'Personal Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('first_name', isRTL ? 'الاسم الأول (English)' : 'First Name (English)', 'text', { required: !formData.first_name_ar })}
                  {renderField('first_name_ar', isRTL ? 'الاسم الأول (عربي)' : 'First Name (Arabic)', 'text', { required: !formData.first_name })}
                  {renderField('middle_name', isRTL ? 'الاسم الأوسط (English)' : 'Middle Name (English)')}
                  {renderField('middle_name_ar', isRTL ? 'الاسم الأوسط (عربي)' : 'Middle Name (Arabic)')}
                  {renderField('last_name', isRTL ? 'اسم العائلة (English)' : 'Last Name (English)', 'text', { required: !formData.last_name_ar })}
                  {renderField('last_name_ar', isRTL ? 'اسم العائلة (عربي)' : 'Last Name (Arabic)', 'text', { required: !formData.last_name })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {renderField('date_of_birth', isRTL ? 'تاريخ الميلاد' : 'Date of Birth', 'date')}
                  
                  <div className="space-y-2">
                    <Label>{isRTL ? 'الجنس' : 'Gender'}</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => (
                          <SelectItem key={g.value} value={g.value}>{getLabel(g)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className={cn(errors.nationality && 'text-red-500')}>
                      {isRTL ? 'الجنسية' : 'Nationality'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.nationality} 
                      onValueChange={(v) => handleChange('nationality', v)}
                    >
                      <SelectTrigger className={cn(errors.nationality && 'border-red-500')}>
                        <SelectValue placeholder={isRTL ? 'اختر الجنسية' : 'Select nationality'} />
                      </SelectTrigger>
                      <SelectContent>
                        {NATIONALITIES.map(n => (
                          <SelectItem key={n.value} value={n.value}>{getLabel(n)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.nationality && (
                      <p className="text-sm text-red-500">{errors.nationality}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('phone', isRTL ? 'رقم الجوال' : 'Mobile Number', 'tel', { required: true, placeholder: '05XXXXXXXX' })}
                  {renderField('personal_email', isRTL ? 'البريد الإلكتروني' : 'Personal Email', 'email', { placeholder: 'email@example.com' })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('address', isRTL ? 'العنوان' : 'Address')}
                  {renderField('city', isRTL ? 'المدينة' : 'City')}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Identity Tab */}
          <TabsContent value="identity" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'وثائق الهوية' : 'Identity Documents'}</CardTitle>
                <CardDescription>
                  {isRTL 
                    ? 'الحقول الحساسة مشفرة. أدخل قيمة جديدة لاستبدال الحالية.' 
                    : 'Sensitive fields are encrypted. Enter a new value to replace current.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* National ID */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700 flex items-center gap-2">
                    {isRTL ? 'الهوية الوطنية' : 'National ID'}
                    <span className="text-xs text-slate-500">
                      {isRTL ? `الحالي: ${originalData.employee?.identity?.national_id_masked || 'غير محدد'}` : `Current: ${originalData.employee?.identity?.national_id_masked || 'Not set'}`}
                    </span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('national_id', isRTL ? 'رقم جديد (اختياري)' : 'New Number (optional)', 'text', { 
                      placeholder: '1XXXXXXXXX'
                    })}
                    {renderField('national_id_expiry', isRTL ? 'تاريخ الانتهاء' : 'Expiry Date', 'date')}
                  </div>
                </div>

                {/* Iqama */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700 flex items-center gap-2">
                    {isRTL ? 'الإقامة' : 'Iqama'}
                    <span className="text-xs text-slate-500">
                      {isRTL ? `الحالي: ${originalData.employee?.identity?.iqama_masked || 'غير محدد'}` : `Current: ${originalData.employee?.identity?.iqama_masked || 'Not set'}`}
                    </span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('iqama', isRTL ? 'رقم جديد (اختياري)' : 'New Number (optional)', 'text', {
                      placeholder: '2XXXXXXXXX'
                    })}
                    {renderField('iqama_expiry', isRTL ? 'تاريخ الانتهاء' : 'Expiry Date', 'date')}
                  </div>
                </div>

                {/* Passport */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700">
                    {isRTL ? 'جواز السفر' : 'Passport'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('passport_number', isRTL ? 'رقم الجواز' : 'Passport Number')}
                    {renderField('passport_expiry', isRTL ? 'تاريخ الانتهاء' : 'Expiry Date', 'date')}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'معلومات التوظيف' : 'Employment Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('employee_number', isRTL ? 'رقم الموظف' : 'Employee Number', 'text')}
                  {renderField('hire_date', isRTL ? 'تاريخ التعيين' : 'Hire Date', 'date', { required: true })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('job_title', isRTL ? 'المسمى الوظيفي (English)' : 'Job Title (English)', 'text', { required: !formData.job_title_ar })}
                  {renderField('job_title_ar', isRTL ? 'المسمى الوظيفي (عربي)' : 'Job Title (Arabic)')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phase 24.9: Consolidated Hierarchical Department Dropdown */}
                  <div className="space-y-2">
                    <Label className={cn(errors.department && 'text-red-500')}>
                      {isRTL ? 'القسم' : 'Department'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.department_id || formData.department} 
                      onValueChange={(v) => {
                        // Find department and auto-fill both legacy and new fields
                        const dept = departmentsList.find(d => d.id === v);
                        if (dept) {
                          handleChange('department_id', v);
                          handleChange('department', dept.name_en || dept.name_ar);
                          handleChange('department_ar', dept.name_ar || dept.name_en);
                        } else {
                          // Fallback for legacy departments
                          handleChange('department', v);
                        }
                      }}
                      disabled={loadingDepartments}
                    >
                      <SelectTrigger 
                        className={cn(errors.department && 'border-red-500')}
                        data-testid="select-department"
                      >
                        <SelectValue placeholder={isRTL ? 'اختر القسم' : 'Select department'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {departmentsList.length > 0 ? (
                          departmentsList.map(dept => (
                            <SelectItem 
                              key={dept.id} 
                              value={dept.id}
                              className={cn(
                                dept.isMain && "font-semibold bg-slate-50",
                                dept.level === 1 && "ps-6 text-sm"
                              )}
                            >
                              {dept.level === 1 && "└ "}
                              {isRTL && dept.name_ar ? dept.name_ar : dept.name_en}
                              {dept.code && <span className="text-slate-400 ms-2 text-xs">({dept.code})</span>}
                            </SelectItem>
                          ))
                        ) : (
                          // Fallback to hardcoded departments if hierarchy not loaded
                          DEPARTMENTS.map(dept => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {errors.department && (
                      <p className="text-sm text-red-500">{errors.department}</p>
                    )}
                  </div>
                  {renderField('department_ar', isRTL ? 'القسم (عربي)' : 'Department (Arabic)')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      {isRTL ? 'الحالة' : 'Status'}
                      <Shield className="h-3 w-3 text-amber-500" />
                    </Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(v) => handleChange('status', v)}
                      disabled={!canEditSensitive}
                    >
                      <SelectTrigger data-testid="select-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{getLabel(s)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{isRTL ? 'نوع العقد' : 'Contract Type'}</Label>
                    <Select 
                      value={formData.contract_type} 
                      onValueChange={(v) => handleChange('contract_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTRACT_TYPES.map(ct => (
                          <SelectItem key={ct.value} value={ct.value}>{getLabel(ct)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('contract_start_date', isRTL ? 'بداية العقد' : 'Contract Start', 'date')}
                  {renderField('contract_end_date', isRTL ? 'نهاية العقد' : 'Contract End', 'date')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('location', isRTL ? 'موقع العمل' : 'Work Location')}
                  
                  {/* Phase 22: Daily Working Hours */}
                  <div className="space-y-2">
                    <Label className={cn(errors.daily_working_hours && 'text-red-500')}>
                      {isRTL ? 'ساعات العمل اليومية' : 'Daily Working Hours'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.daily_working_hours} 
                      onValueChange={(v) => handleChange('daily_working_hours', v)}
                    >
                      <SelectTrigger 
                        className={cn(errors.daily_working_hours && 'border-red-500')}
                        data-testid="select-daily-working-hours"
                      >
                        <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">{isRTL ? '8 ساعات/يوم' : '8 hours/day'}</SelectItem>
                        <SelectItem value="10">{isRTL ? '10 ساعات/يوم' : '10 hours/day'}</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.daily_working_hours && (
                      <p className="text-sm text-red-500">{errors.daily_working_hours}</p>
                    )}
                  </div>
                </div>

                {/* Phase 24.8: Branch Assignment (HR Admin/Super Admin only) */}
                {canEditSensitive && (
                  <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg space-y-4">
                    <h4 className="font-medium text-purple-800 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {isRTL ? 'الفرع' : 'Branch'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Branch Selection */}
                      <div className="space-y-2">
                        <Label>{isRTL ? 'فرع العمل' : 'Work Branch'}</Label>
                        <Select 
                          value={formData.branch_id || ''} 
                          onValueChange={(v) => handleChange('branch_id', v === '_none_' ? '' : v)}
                          disabled={loadingBranches}
                        >
                          <SelectTrigger data-testid="select-branch-id">
                            <SelectValue placeholder={isRTL ? 'اختر الفرع' : 'Select branch'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none_">{isRTL ? '-- لا يوجد --' : '-- None --'}</SelectItem>
                            {branchesList.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>
                                {isRTL ? (branch.name_ar || branch.name_en) : (branch.name_en || branch.name_ar)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          {isRTL 
                            ? 'الفرع الذي يعمل فيه الموظف' 
                            : 'The branch where the employee works'}
                        </p>
                      </div>

                      {/* Inline Branch Creation */}
                      <div className="space-y-2">
                        <Label>{isRTL ? 'إنشاء فرع جديد' : 'Create New Branch'}</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder={isRTL ? 'اسم الفرع الجديد' : 'New branch name'}
                            value={newBranchName}
                            onChange={(e) => setNewBranchName(e.target.value)}
                            data-testid="input-new-branch-name"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCreateBranch}
                            disabled={!newBranchName.trim() || creatingBranch}
                            data-testid="btn-create-branch"
                          >
                            {creatingBranch ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              isRTL ? 'إضافة' : 'Add'
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          {isRTL 
                            ? 'أو أنشئ فرعًا جديدًا إذا لم يكن موجودًا' 
                            : 'Or create a new branch if not available'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Annual leave entitlement + accrual dates (2026-09-17).
                    HR Manager / HR Admin / Super Admin only. */}
                {canEditAnnualLeave && (
                  <AnnualConfigurationSummary employeeId={id} />
                )}

                {/* Phase 18.2.2: Approving Manager (HR Admin/Super Admin only) */}
                {canEditSensitive && (
                  <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-4">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {isRTL ? 'التسلسل الإداري' : 'Organizational Hierarchy'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Direct Manager */}
                      <div className="space-y-2">
                        <Label>{isRTL ? 'المدير المباشر' : 'Direct Manager'}</Label>
                        <ManagerSelect
                          value={formData.manager_id || ''}
                          onChange={(v) => handleChange('manager_id', v)}
                          employees={employees}
                          loading={loadingEmployees}
                          placeholder={isRTL ? 'اختر المدير' : 'Select manager'}
                          noneLabel={isRTL ? '— لا يوجد —' : '— None —'}
                          testId="select-manager-id"
                          fallbackEmployee={currentManagerFallback}
                        />
                        <p className="text-xs text-slate-500">
                          {isRTL ? 'مدير التقارير اليومية' : 'Day-to-day reporting manager'}
                        </p>
                      </div>

                      {/* Approving Manager */}
                      <div className="space-y-2">
                        <Label>{isRTL ? 'مدير الموافقات (اختياري)' : 'Approving Manager (Optional)'}</Label>
                        <ManagerSelect
                          value={formData.approving_manager_id || ''}
                          onChange={(v) => handleChange('approving_manager_id', v)}
                          employees={employees}
                          loading={loadingEmployees}
                          placeholder={isRTL ? 'يستخدم المدير المباشر' : 'Uses Direct Manager'}
                          noneLabel={isRTL ? '— يستخدم المدير المباشر —' : '— Use Direct Manager —'}
                          testId="select-approving-manager-id"
                          fallbackEmployee={currentApprovingManagerFallback}
                        />
                        <p className="text-xs text-slate-500">
                          {isRTL 
                            ? 'يستخدم لتوجيه طلبات الإجازة والطلبات العامة. إذا لم يُحدد، يُستخدم المدير المباشر.' 
                            : 'Used for Leave & General Request routing. If not set, Direct Manager is used.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Tab */}
          <TabsContent value="bank" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'المعلومات البنكية' : 'Bank Information'}</CardTitle>
                <CardDescription>
                  {isRTL ? `الآيبان الحالي: ${originalData.employee?.bank?.iban_masked || 'غير محدد'}` : `Current IBAN: ${originalData.employee?.bank?.iban_masked || 'Not set'}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'اسم البنك' : 'Bank Name'}</Label>
                    <Select 
                      value={formData.bank_name} 
                      onValueChange={(v) => handleChange('bank_name', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isRTL ? 'اختر البنك' : 'Select bank'} />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKS.map(bank => (
                          <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {renderField('iban', isRTL ? 'آيبان جديد (اختياري)' : 'New IBAN (optional)', 'text', { placeholder: 'SA0000000000000000000000' })}
                </div>
                {renderField('account_holder_name', isRTL ? 'اسم صاحب الحساب' : 'Account Holder Name')}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compensation Tab */}
          <TabsContent value="compensation" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isRTL ? 'التعويضات' : 'Compensation'}
                  <Shield className="h-4 w-4 text-amber-500" />
                </CardTitle>
                <CardDescription>
                  {isRTL ? 'للتخزين فقط - تغيير هذه الحقول يتطلب سبباً' : 'Storage only - changes require a reason'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('basic_salary', isRTL ? 'الراتب الأساسي (ريال)' : 'Basic Salary (SAR)', 'number', { placeholder: '0.00' })}
                  {renderField('housing_allowance', isRTL ? 'بدل السكن (ريال)' : 'Housing Allowance (SAR)', 'number', { placeholder: '0.00' })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('transportation_allowance', isRTL ? 'بدل النقل (ريال)' : 'Transportation Allowance (SAR)', 'number', { placeholder: '0.00' })}
                  {renderField('other_allowances', isRTL ? 'بدلات أخرى (ريال)' : 'Other Allowances (SAR)', 'number', { placeholder: '0.00' })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase 18.8.1: Custom Fields Tab */}
          {customFieldDefinitions.length > 0 && (
            <TabsContent value="custom" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    {isRTL ? 'الحقول المخصصة' : 'Custom Fields'}
                  </CardTitle>
                  <CardDescription>
                    {isRTL 
                      ? 'الحقول المخصصة المحددة لمنظمتك'
                      : 'Organization-specific custom fields'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingCustomFields ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {customFieldDefinitions.map(field => (
                        <div key={field.id} className="space-y-2">
                          <Label>
                            {isRTL ? (field.label_ar || field.label_en) : field.label_en}
                            {field.is_required && <span className="text-red-500 ms-1">*</span>}
                          </Label>
                          
                          {/* Text field */}
                          {field.data_type === 'text' && (
                            <Input
                              value={customFieldValues[field.key] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.key]: e.target.value
                              }))}
                              placeholder={field.description || ''}
                              disabled={!canEditCustomFields}
                            />
                          )}
                          
                          {/* Number field */}
                          {field.data_type === 'number' && (
                            <Input
                              type="number"
                              value={customFieldValues[field.key] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.key]: e.target.value ? parseFloat(e.target.value) : null
                              }))}
                              placeholder={field.description || ''}
                              disabled={!canEditCustomFields}
                            />
                          )}
                          
                          {/* Date field */}
                          {field.data_type === 'date' && (
                            <Input
                              type="date"
                              value={customFieldValues[field.key] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.key]: e.target.value
                              }))}
                              disabled={!canEditCustomFields}
                            />
                          )}
                          
                          {/* Boolean field */}
                          {field.data_type === 'boolean' && (
                            <div 
                              className={cn(
                                "flex items-center gap-2 p-2 rounded-lg border cursor-pointer",
                                !canEditCustomFields && "opacity-50 cursor-not-allowed"
                              )}
                              onClick={() => {
                                if (canEditCustomFields) {
                                  setCustomFieldValues(prev => ({
                                    ...prev,
                                    [field.key]: !prev[field.key]
                                  }));
                                }
                              }}
                            >
                              {customFieldValues[field.key] ? (
                                <ToggleRight className="h-6 w-6 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-6 w-6 text-slate-400" />
                              )}
                              <span className="text-sm">
                                {customFieldValues[field.key] 
                                  ? (isRTL ? 'نعم' : 'Yes') 
                                  : (isRTL ? 'لا' : 'No')}
                              </span>
                            </div>
                          )}
                          
                          {/* Dropdown field */}
                          {field.data_type === 'dropdown' && (
                            <Select
                              value={customFieldValues[field.key] || ''}
                              onValueChange={(v) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.key]: v
                              }))}
                              disabled={!canEditCustomFields}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={isRTL ? 'اختر...' : 'Select...'} />
                              </SelectTrigger>
                              <SelectContent>
                                {(field.dropdown_options || []).map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          
                          {field.description && (
                            <p className="text-xs text-slate-500">{field.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!canEditCustomFields && (
                    <p className="text-sm text-amber-600 mt-4">
                      {isRTL 
                        ? 'ليس لديك صلاحية لتعديل الحقول المخصصة'
                        : 'You do not have permission to edit custom fields'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/employees/${id}`)}
            disabled={saving}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            data-testid="submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {isRTL ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 me-2" />
                {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Reason Dialog for Sensitive Changes */}
      <Dialog open={reasonDialog} onOpenChange={(open) => !open && setReasonDialog(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              {isRTL ? 'سبب التغيير مطلوب' : 'Reason Required'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'أنت تقوم بتغيير حقول حساسة. يرجى إدخال سبب لهذا التغيير.'
                : 'You are changing sensitive fields. Please provide a reason for this change.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="change-reason">
                {isRTL ? 'سبب التغيير (مطلوب)' : 'Reason for change (required)'}
              </Label>
              <Textarea
                id="change-reason"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب التغيير (10 أحرف على الأقل)...' : 'Enter reason for change (at least 10 characters)...'}
                rows={3}
                data-testid="change-reason-input"
              />
              {changeReason.length > 0 && changeReason.length < 10 && (
                <p className="text-sm text-amber-600">
                  {isRTL ? `${10 - changeReason.length} أحرف متبقية` : `${10 - changeReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReasonDialog(false);
                setPendingChanges(null);
                setChangeReason('');
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleReasonConfirm}
              disabled={changeReason.length < 10 || saving}
              data-testid="confirm-reason-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
              {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeEditPage;
